import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface CookiePreferencesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type CookiePreferences = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

export const CookiePreferencesDialog = ({
  open,
  onOpenChange,
}: CookiePreferencesDialogProps) => {
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  // Fetch current preferences when dialog opens
  useEffect(() => {
    if (open) {
      const fetchPreferences = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data } = await supabase
            .from('profiles')
            .select('cookie_preferences')
            .eq('id', user.id)
            .single();
          
          if (data?.cookie_preferences) {
            const savedPrefs = data.cookie_preferences as CookiePreferences;
            setPreferences({
              necessary: true, // Always true
              analytics: savedPrefs.analytics || false,
              marketing: savedPrefs.marketing || false,
            });
          }
        }
      };
      fetchPreferences();
    }
  }, [open]);

  const handleSave = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('profiles')
          .update({
            cookie_preferences: preferences
          })
          .eq('id', user.id);
      }
      onOpenChange(false);
      toast.success("Préférences de cookies enregistrées");
    } catch (error) {
      console.error("Erreur lors de l'enregistrement des préférences:", error);
      toast.error("Erreur lors de l'enregistrement des préférences");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Préférences des cookies</DialogTitle>
          <DialogDescription>
            Gérez vos préférences de cookies conformément au RGPD et au CCPA
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="cookies" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="cookies">Cookies</TabsTrigger>
            <TabsTrigger value="about">À propos</TabsTrigger>
          </TabsList>

          <TabsContent value="cookies" className="space-y-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between space-x-2">
                <div className="space-y-0.5">
                  <Label>Cookies nécessaires</Label>
                  <p className="text-sm text-muted-foreground">
                    Ces cookies sont essentiels au fonctionnement du site
                  </p>
                </div>
                <Switch checked disabled />
              </div>

              <div className="flex items-center justify-between space-x-2">
                <div className="space-y-0.5">
                  <Label>Cookies analytiques</Label>
                  <p className="text-sm text-muted-foreground">
                    Nous aident à comprendre comment vous utilisez le site
                  </p>
                </div>
                <Switch
                  checked={preferences.analytics}
                  onCheckedChange={(checked) =>
                    setPreferences((prev) => ({ ...prev, analytics: checked }))
                  }
                />
              </div>

              <div className="flex items-center justify-between space-x-2">
                <div className="space-y-0.5">
                  <Label>Cookies marketing</Label>
                  <p className="text-sm text-muted-foreground">
                    Utilisés pour la publicité ciblée et le marketing
                  </p>
                </div>
                <Switch
                  checked={preferences.marketing}
                  onCheckedChange={(checked) =>
                    setPreferences((prev) => ({ ...prev, marketing: checked }))
                  }
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="about" className="space-y-4">
            <div className="space-y-4">
              <div>
                <h4 className="font-medium">RGPD (GDPR)</h4>
                <p className="text-sm text-muted-foreground">
                  Le Règlement Général sur la Protection des Données (RGPD) est une 
                  réglementation de l'Union européenne qui renforce et unifie la 
                  protection des données personnelles. Il exige un consentement 
                  explicite pour la collecte et l'utilisation des données.
                </p>
              </div>

              <div>
                <h4 className="font-medium">CCPA</h4>
                <p className="text-sm text-muted-foreground">
                  Le California Consumer Privacy Act (CCPA) est une loi californienne 
                  qui donne aux résidents de la Californie des droits spécifiques 
                  concernant leurs données personnelles, notamment le droit de savoir 
                  quelles informations sont collectées et de refuser la vente de 
                  leurs données.
                </p>
              </div>

              <div>
                <h4 className="font-medium">Vos droits</h4>
                <p className="text-sm text-muted-foreground">
                  Vous avez le droit d'accéder, de corriger et de supprimer vos 
                  données, ainsi que de vous opposer au traitement de vos données 
                  et de retirer votre consentement à tout moment.
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={handleSave}>
            Enregistrer les préférences
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};